/**
 * A payment refused before any request went out, because something the tenant requires is missing.
 *
 * Kept in its own module, apart from `createPayment`, so a brand's precondition can build one
 * without importing the module that imports it back.
 *
 * Every field is an own enumerable property, not a getter or a prototype member: several callers
 * copy the error into a plain object (`{ ...error, paymentEngine }`) before it reaches
 * `paymentFailureHandler`, and anything the spread does not carry would be lost on the way.
 */
export class PaymentPreconditionError extends Error {
  /**
   * @param {Object} options - What the failure handler needs.
   * @param {string} options.code - Stable identifier for the precondition that failed.
   * @param {string} options.notificationKey - The `setError` key shown to the buyer.
   * @param {Function} [options.followUp] - Action creator dispatched after the notification.
   * @param {string} [options.message] - Developer-facing message.
   */
  constructor({ code, notificationKey, followUp = null, message = 'Payment precondition failed' }) {
    super(message);
    this.name = 'PaymentPreconditionError';
    this.isPaymentPreconditionError = true;
    this.code = code;
    this.notificationKey = notificationKey;
    this.followUp = followUp;
  }
}

/**
 * Whether the error is a precondition refusal, including one already spread into a plain object.
 *
 * A field check rather than `instanceof`, which a spread copy fails.
 * @param {*} error - Whatever the payment promise rejected with.
 * @returns {boolean} True for a precondition refusal.
 */
export function isPaymentPreconditionError(error) {
  return error?.isPaymentPreconditionError === true;
}
